import type {
  GovernedLibraryDocumentSummary,
  LibraryDocumentCanonicalState,
} from "../api/cognitoryLibraryClient";
import { mapVersionStatusToUserVisible } from "./cognitoryLibraryIngestionStatus";

export function excludeDocumentFromLibraryList(input: {
  documents: GovernedLibraryDocumentSummary[];
  documentId: string;
}): GovernedLibraryDocumentSummary[] {
  return input.documents.filter(
    document => document.documentId !== input.documentId,
  );
}

export function upsertLibraryDocument(input: {
  documents: GovernedLibraryDocumentSummary[];
  document: GovernedLibraryDocumentSummary;
}): GovernedLibraryDocumentSummary[] {
  const without = input.documents.filter(
    document => document.documentId !== input.document.documentId,
  );
  return [input.document, ...without];
}

export function shouldApplyLibraryFetchResult(input: {
  requestGeneration: number;
  activeGeneration: number;
}): boolean {
  return input.requestGeneration === input.activeGeneration;
}

function isTerminalForListMerge(input: {
  canonicalState?: LibraryDocumentCanonicalState | undefined;
  versionStatus: string | null;
}): boolean {
  if (input.canonicalState === "answerable") {
    return true;
  }
  if (!input.canonicalState) {
    const userVisible = mapVersionStatusToUserVisible(input.versionStatus);
    return userVisible === "ready" || userVisible === "processing_failed";
  }
  return false;
}

export function shouldPreferIncomingDocumentVersion(input: {
  currentVersionStatus: string | null;
  incomingVersionStatus: string | null;
  currentCanonicalState?: LibraryDocumentCanonicalState | undefined;
  incomingCanonicalState?: LibraryDocumentCanonicalState | undefined;
}): boolean {
  if (
    input.currentCanonicalState === "terminal_failed" &&
    input.incomingCanonicalState === "answerable"
  ) {
    return true;
  }
  if (
    isTerminalForListMerge({
      canonicalState: input.currentCanonicalState,
      versionStatus: input.currentVersionStatus,
    })
  ) {
    return false;
  }
  if (
    isTerminalForListMerge({
      canonicalState: input.incomingCanonicalState,
      versionStatus: input.incomingVersionStatus,
    })
  ) {
    return true;
  }
  return true;
}

export function mergeLibraryDocumentList(input: {
  current: GovernedLibraryDocumentSummary[];
  incoming: GovernedLibraryDocumentSummary[];
  excludedDocumentIds?: ReadonlySet<string>;
  monotonicDocumentIds?: ReadonlySet<string>;
}): GovernedLibraryDocumentSummary[] {
  const excluded = input.excludedDocumentIds ?? new Set<string>();
  const currentById = new Map(
    input.current.map(document => [document.documentId, document]),
  );
  const merged: GovernedLibraryDocumentSummary[] = [];

  for (const incoming of input.incoming) {
    if (excluded.has(incoming.documentId)) {
      continue;
    }
    const existing = currentById.get(incoming.documentId);
    if (
      existing &&
      !shouldPreferIncomingDocumentVersion({
        currentVersionStatus: existing.versionStatus,
        incomingVersionStatus: incoming.versionStatus,
        currentCanonicalState: existing.canonicalState,
        incomingCanonicalState: incoming.canonicalState,
      })
    ) {
      merged.push(existing);
      currentById.delete(incoming.documentId);
      continue;
    }
    merged.push(incoming);
    currentById.delete(incoming.documentId);
  }

  for (const remaining of currentById.values()) {
    if (!excluded.has(remaining.documentId)) {
      merged.push(remaining);
    }
  }

  return merged;
}
